"use client";

import React, { createContext, useContext, useState, useEffect } from "react";
import { AuthService, User } from "@/server/auth";

const TOKEN_KEY = "ittelecom_access_token";
const REFRESH_TOKEN_KEY = "ittelecom_refresh_token";
const USER_KEY = "ittelecom_user";
const LOGGED_IN_COOKIE = "ittelecom_logged_in";

// کوکی ساده فقط برای اینکه سرور بداند دکمه چه شکلی رندر شود (نه برای امنیت)
const setLoggedInCookie = () => {
  document.cookie = `${LOGGED_IN_COOKIE}=1; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`;
};
const clearLoggedInCookie = () => {
  document.cookie = `${LOGGED_IN_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
};

interface AuthContextType {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (accessToken: string, refreshToken: string, user: User) => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  openAuthModal: (defaultTab?: "login" | "signup") => void;
  authModalOpen: boolean;
  closeAuthModal: () => void;
  isInWishlist: (productId: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({
                               children,
                               initialAuthenticated = false,
                             }: {
  children: React.ReactNode;
  initialAuthenticated?: boolean;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authModalOpen, setAuthModalOpen] = useState(false);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const token = localStorage.getItem(TOKEN_KEY);
        const storedUser = localStorage.getItem(USER_KEY);

        if (token && storedUser) {
          // کاربرهای قبلی که کوکی ندارند: کوکی را همین‌جا ست کن
          setLoggedInCookie();

          setAccessToken(token);
          setUser(JSON.parse(storedUser));

          try {
            const freshUser = await AuthService.getProfile();
            setUser(freshUser);
            localStorage.setItem(USER_KEY, JSON.stringify(freshUser));
          } catch (error: any) {
            const status = error?.response?.status;
            console.error("Error fetching user profile:", status, error?.response?.data);

            if (status === 401) {
              const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
              if (refreshToken) {
                try {
                  const { accessToken: newAccessToken } = await AuthService.refreshToken({ refreshToken });
                  localStorage.setItem(TOKEN_KEY, newAccessToken);
                  setAccessToken(newAccessToken);
                  const freshUser = await AuthService.getProfile();
                  setUser(freshUser);
                  localStorage.setItem(USER_KEY, JSON.stringify(freshUser));
                } catch (refreshError) {
                  console.error("Token refresh failed:", refreshError);
                  localStorage.removeItem(TOKEN_KEY);
                  localStorage.removeItem(REFRESH_TOKEN_KEY);
                  localStorage.removeItem(USER_KEY);
                  clearLoggedInCookie();
                  setAccessToken(null);
                  setUser(null);
                }
              } else {
                localStorage.removeItem(TOKEN_KEY);
                localStorage.removeItem(USER_KEY);
                clearLoggedInCookie();
                setAccessToken(null);
                setUser(null);
              }
            }
          }
        } else {
          // توکن نیست ولی کوکی مانده (مثلاً localStorage پاک شده)
          clearLoggedInCookie();
        }
      } catch (error) {
        console.error("Error loading user:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUser();
  }, []);

  const login = (token: string, refreshToken: string, userData: User) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
    setLoggedInCookie();
    setAccessToken(token);
    setUser(userData);
  };

  const logout = async () => {
    try {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (refreshToken) {
        await AuthService.logout({ refreshToken });
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      clearLoggedInCookie();
      setAccessToken(null);
      setUser(null);
    }
  };

  const refreshUser = async () => {
    try {
      const freshUser = await AuthService.getProfile();
      setUser(freshUser);
      localStorage.setItem(USER_KEY, JSON.stringify(freshUser));
    } catch (error) {
      console.error("Error refreshing user:", error);
    }
  };

  // ورود/ثبت‌نام فقط با شماره موبایل در صفحه‌های /auth انجام می‌شود (مودال قدیمی ایمیلی حذف شد)
  const openAuthModal = (defaultTab: "login" | "signup" = "login") => {
    window.location.href = defaultTab === "signup" ? "/auth/register" : "/auth/login";
  };

  const closeAuthModal = () => setAuthModalOpen(false);

  const isInWishlist = (productId: string): boolean => {
    if (!user || !user.wishlistProductIds) return false;
    return user.wishlistProductIds.includes(productId);
  };

  // تا وقتی چک تمام نشده، از مقدار کوکی (که سرور هم دیده) استفاده کن
  // بعد از پایان چک، مقدار واقعی جایگزین می‌شود
  const isAuthenticated = isLoading ? initialAuthenticated : !!user;

  return (
      <AuthContext.Provider
          value={{
            user, accessToken, isAuthenticated, isLoading,
            login, logout, refreshUser,
            openAuthModal, authModalOpen, closeAuthModal, isInWishlist,
          }}
      >
        {children}
      </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error("useAuth must be used within an AuthProvider");
  return context;
}

export function getAuthToken(): string | null {
  if (typeof window !== "undefined") return localStorage.getItem(TOKEN_KEY);
  return null;
}